import React, { useState, useEffect } from 'react';
import { X, Sparkles, MapPin, ArrowRight, Loader2, Check, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { jobChanged, Actions } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

// 12-week report groups by worker + round; the 4-week view stays a flat list.
const groupSuggestions = (list, weeks) => {
  if (weeks < 12) return [['', list]];
  const groups = new Map();
  list.forEach((s) => {
    const key = `${s.assignee || 'Unassigned'}${s.round ? ' — ' + s.round : ''}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });
  return [...groups.entries()];
};

// Round Optimiser — lists outlier jobs that geographically fit better on a
// nearby day, with one-tap accept (permanent re-anchor) or dismiss.
export default function RoundOptimizerModal({ onClose, onApplied }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [weeks, setWeeks] = useState(4);

  useEffect(() => {
    let stale = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(
          `${API_URL}/api/optimizer/suggestions?horizon_days=${weeks * 7}&max_results=${weeks >= 12 ? 500 : 20}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to load suggestions');
        const data = await res.json();
        if (!stale) setSuggestions(data.suggestions || []);
      } catch (e) {
        if (!stale) setError('Could not analyse the round. Please try again.');
      } finally {
        if (!stale) setLoading(false);
      }
    })();
    return () => { stale = true; };
  }, [weeks]);

  const accept = async (s) => {
    setBusyId(s.job_id);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/optimizer/suggestions/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: s.job_id,
          new_date: s.suggested_date,
          original_date: s.current_date,
          mode: 'permanent'
        })
      });
      if (!res.ok) throw new Error('accept failed');
      setSuggestions((prev) => prev.filter((x) => x.job_id !== s.job_id));
      jobChanged(Actions.UPDATE, { id: s.job_id });
      if (onApplied) onApplied();
      toast.success(`${s.customer_name} moved to ${fmtDate(s.suggested_date)}`);
    } catch {
      toast.error('Could not move the job — please try again');
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (s) => {
    setBusyId(s.job_id);
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/optimizer/suggestions/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: s.job_id })
      });
      setSuggestions((prev) => prev.filter((x) => x.job_id !== s.job_id));
      if (onApplied) onApplied();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg modal-shell-h flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="round-optimizer-modal"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Sparkles size={22} />
              </div>
              <div>
                <h2 className="text-lg font-bold">Round Optimiser</h2>
                <p className="text-white/80 text-xs">Tighten each day&apos;s work geographically</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-testid="optimizer-close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {[[4, 'Next 4 weeks'], [12, '12-week report']].map(([w, label]) => (
              <button
                key={w}
                onClick={() => setWeeks(w)}
                data-testid={`optimizer-range-${w}w`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  weeks === w ? 'bg-white text-indigo-700' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {label}
              </button>
            ))}
            {!loading && suggestions && suggestions.length > 0 && (
              <span className="ml-auto text-white/85 text-xs font-medium" data-testid="optimizer-summary">
                {suggestions.length} move{suggestions.length !== 1 ? 's' : ''}
                {(() => {
                  const mi = suggestions.reduce((t, s) => t + (s.saving_km || 0), 0) * 0.621371;
                  return mi >= 0.1 ? ` · ~${mi.toFixed(1)} mi saved` : '';
                })()}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Loader2 size={28} className="animate-spin mb-3" />
              <p className="text-sm">Analysing your round…</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>
          )}

          {!loading && !error && suggestions && suggestions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="optimizer-empty-state">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <Check size={28} className="text-emerald-600" />
              </div>
              <p className="font-semibold text-zinc-800">Your round is tight</p>
              <p className="text-sm text-zinc-500 mt-1 max-w-xs">
                No jobs found that would sit closer to a cluster on a nearby day (±7 days).
              </p>
            </div>
          )}

          {!loading && suggestions && groupSuggestions(suggestions, weeks).map(([groupLabel, items]) => (
            <div key={groupLabel || 'all'}>
              {groupLabel && (
                <p className="text-[11px] font-bold uppercase tracking-wide text-zinc-400 mt-2 mb-1.5 px-0.5" data-testid={`optimizer-group-${groupLabel}`}>
                  {groupLabel} ({items.length})
                </p>
              )}
              <div className="space-y-3">
              {items.map((s) => (
            <div key={s.job_id} className="border border-zinc-200 rounded-xl p-4 bg-white" data-testid={`optimizer-suggestion-${s.job_id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-900 truncate">{s.customer_name}</p>
                  <p className="text-xs text-zinc-500 flex items-center gap-1 truncate">
                    <MapPin size={12} className="flex-shrink-0" /> {s.address}
                  </p>
                </div>
                {s.frequency && (
                  <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">
                    {String(s.frequency).match(/^\d+$/) ? `${s.frequency} weekly` : s.frequency}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm mb-1.5">
                <span className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 font-medium whitespace-nowrap">
                  {fmtDate(s.current_date)}
                </span>
                <ArrowRight size={14} className="text-zinc-400 flex-shrink-0" />
                <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-medium whitespace-nowrap">
                  {fmtDate(s.suggested_date)}
                </span>
              </div>

              <p className="text-xs text-zinc-600 mb-3">
                {s.reason ? (
                  <span data-testid={`optimizer-reason-${s.job_id}`}>
                    {s.tier === 'street' && (
                      <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 mr-1.5 align-middle">Same street</span>
                    )}
                    {s.reason}
                  </span>
                ) : (
                  <>
                    {s.due_in_days != null && (
                      <>Due in <span className="font-semibold">{s.due_in_days} day{s.due_in_days === 1 ? '' : 's'}</span></>
                    )}
                    {s.lone_job
                      ? ' — only job in that area on its day.'
                      : ` — nearest job that day is ${s.current_nearest_mi} mi away.`}{' '}
                    Fits nicely on {fmtDate(s.suggested_date)}: <span className="font-semibold">{s.suggested_nearest_mi} mi</span> from{' '}
                    {s.nearest_customer_name}
                    {s.suggested_nearby_count > 1 ? ` with ${s.suggested_nearby_count} jobs nearby` : ''}.
                  </>
                )}
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => accept(s)}
                  disabled={busyId === s.job_id}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid={`optimizer-accept-${s.job_id}`}
                >
                  {busyId === s.job_id ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                  Move it
                </button>
                <button
                  onClick={() => dismiss(s)}
                  disabled={busyId === s.job_id}
                  className="px-3 py-2 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 text-zinc-600 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5"
                  data-testid={`optimizer-dismiss-${s.job_id}`}
                >
                  <EyeOff size={15} />
                  Dismiss
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-2">
                Moving re-anchors all future visits from {fmtDate(s.suggested_date)}
                {s.day_shift ? ` (${s.day_shift > 0 ? '+' : ''}${s.day_shift} days)` : ''}.
              </p>
            </div>
              ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
